import { useId } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import ReactPlayer from "react-player";
import GrIconButton from "./GrIconButton";
import GrIcon from "./GrIcon";
import GrLoader from "./GrLoader";
import icons from "../icons";
import { gr } from "../tokens";

// Video player popup: a header (optional badge, title, close ×), an optional meta line (date,
// completed…) and the video in a rounded 16:9 frame that plays straight away (muted, so
// browsers allow autoplay). Close with ×, Esc or the backdrop.
// `loading`: a spinner in the frame while the video's link is still being prepared.
export default function GrVideoDialog({ open, onClose, url, title, badge, meta, onEnded, loading = false }) {
  const titleId = `gr-video-${useId()}`;
  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      aria-labelledby={title ? titleId : undefined}
      BackdropProps={{ sx: { background: gr.color.overlay } }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 960,
          m: gr.space[4],
          borderRadius: gr.radius.card,
          boxShadow: gr.shadow.raised,
          padding: gr.space[5],
          fontFamily: gr.font.family,
          color: gr.color.ink,
          backgroundImage: "none",
          "@media (max-width: 599.95px)": { padding: gr.space[4] },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: gr.space[3],
          mb: gr.space[4],
        }}
      >
        <Box sx={{ minWidth: 0, display: "grid", gap: gr.space[1] }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: gr.space[2] }}>
            {badge}
            {title ? (
              <Box
                component="h2"
                id={titleId}
                sx={{
                  m: 0,
                  fontSize: gr.font.size.xl,
                  fontWeight: gr.font.weight.semibold,
                  lineHeight: gr.font.lineHeight.tight,
                  color: gr.color.ink,
                  overflowWrap: "anywhere",
                }}
              >
                {title}
              </Box>
            ) : null}
          </Box>
          {meta ? (
            <Box sx={{ fontSize: gr.font.size.sm, color: gr.color.muted }}>{meta}</Box>
          ) : null}
        </Box>
        <GrIconButton variant="ghost" aria-label="Close video" onClick={onClose}>
          <GrIcon icon={icons.close} />
        </GrIconButton>
      </Box>
      <Box
        sx={{
          position: "relative",
          aspectRatio: "16 / 9",
          overflow: "hidden",
          borderRadius: gr.radius.tile,
          background: gr.color.ink,
          // Players (Vimeo / YouTube iframes, files) fill the frame even before they are ready.
          "& iframe, & video": {
            position: "absolute",
            inset: 0,
            width: "100% !important",
            height: "100% !important",
            border: 0,
          },
        }}
      >
        {open && loading ? (
          <GrLoader label="Loading video" sx={{ position: "absolute", inset: 0 }} />
        ) : null}
        {open && url && !loading ? (
          <ReactPlayer
            url={url}
            width="100%"
            height="100%"
            style={{ position: "absolute", inset: 0 }}
            controls
            playsinline
            playing
            muted
            onEnded={onEnded}
          />
        ) : null}
      </Box>
    </Dialog>
  );
}

GrVideoDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  url: PropTypes.string,
  title: PropTypes.node,
  badge: PropTypes.node,
  meta: PropTypes.node,
  onEnded: PropTypes.func,
  loading: PropTypes.bool,
};
